import { useEffect, useRef, useState } from "react";
import { getEmbeddableUrl } from "../../services/utils/getEmbeddableUrl.js";
import {
  useGetViewMutation,
  useUpdateViewMutation,
  useGetVideosByApiQuery
} from "../../services/modulesuser/videoListApi";
import Player from "@vimeo/player";
import { useDispatch,useSelector} from "react-redux";
import { setWatchlist } from "../../store/user";
import { selectVar } from "../../store/conf/confSlice.js";

function VideoTrailer({ video, videoId }) {
  const dispatch = useDispatch();
    const updateposition= useSelector(selectVar("_TIME_SAVE_VIDEO",10000));

  const { data, refetch } = useGetVideosByApiQuery("/views/list");

  const [getView] = useGetViewMutation();
  const [updateView] = useUpdateViewMutation();

  const [startTime, setStartTime] = useState(null);

  const iframeRef = useRef(null);
  const videoRef = useRef(null);
  const ytPlayerRef = useRef(null);
  const vimeoPlayerRef = useRef(null);
  const intervalRef = useRef(null);
  const vimeoHandlersRef = useRef({ timeupdate: null, ended: null, pause: null });
  const lastSavedRef = useRef(0);
  const lastTimeValueRef = useRef(0);
  const lastDurationRef = useRef(0);

  const isYouTube = !!video?.includes("youtube.com");
  const isVimeo = !!video?.includes("vimeo.com");
  const isMp4 = !!(video?.endsWith(".mp4") || video?.includes(".mp4?"));
  const embedUrl = getEmbeddableUrl(video);
  const updateIntervalMs = Math.max(Number(updateposition) || 0, 1000);

  // --- Watchlist poll ogni 30s
  useEffect(() => {
    let active = true;
    const fetchAndUpdate = async () => {
      try {
        const result = await refetch();
        if (!active) return;
        if (result.data && Array.isArray(result.data)) {
          dispatch(setWatchlist(result.data));
        }
      } catch (err) {
        console.error("Errore fetch watchlist:", err);
      }
    };
    fetchAndUpdate();
    const id = setInterval(fetchAndUpdate, 30000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [dispatch, refetch]);

  // --- 1) Ottieni lastTime dal server
  useEffect(() => {
    let cancelled = false;
    const fetchStartTime = async () => {
      if (!videoId) {
        setStartTime(0);
        return;
      }
      try {
        const res = await getView({ videoId }).unwrap();
        const lastTime = res?.data?.result?.lastTime ?? 0;
        if (!cancelled) setStartTime(Number(lastTime) || 0);
      } catch {
        if (!cancelled) setStartTime(0);
      }
    };
    fetchStartTime();
    return () => {
      cancelled = true;
    };
  }, [videoId, getView]);

  // --- 2) MP4 nativi: seek quando startTime disponibile
  useEffect(() => {
    if (!isMp4) return;
    if (startTime === null) return;
    if (videoRef.current && Number.isFinite(startTime)) {
      try {
        videoRef.current.currentTime = startTime;
      } catch {}
    }
  }, [isMp4, startTime]);

  // --- 3) Vimeo: gestisci progressi e resume
  useEffect(() => {
    const clearIntervalIfAny = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const detachHandlers = () => {
      if (vimeoPlayerRef.current && vimeoHandlersRef.current) {
        const { timeupdate, pause, ended } = vimeoHandlersRef.current;
        if (timeupdate) vimeoPlayerRef.current.off("timeupdate", timeupdate);
        if (pause) vimeoPlayerRef.current.off("pause", pause);
        if (ended) vimeoPlayerRef.current.off("ended", ended);
      }
      vimeoHandlersRef.current = { timeupdate: null, ended: null, pause: null };
    };

    if (!isVimeo || !iframeRef.current || startTime === null) {
      clearIntervalIfAny();
      detachHandlers();
      if (vimeoPlayerRef.current) {
        vimeoPlayerRef.current.unload?.().catch(() => {});
        vimeoPlayerRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    let localPlayer = vimeoPlayerRef.current;

    const triggerProgressSave = (currentTime, durationValue, force = false) => {
      console.log ("Trigger save:", { currentTime, durationValue, force });
      if (!videoId || cancelled) return;
      const now = Date.now();
      if (!force && now - lastSavedRef.current < updateIntervalMs) return;
      lastSavedRef.current = now;
      lastTimeValueRef.current = currentTime;
      lastDurationRef.current = durationValue;
      updateView({
        videoId,
        completed: false,
        lastTime: currentTime,
        duration: durationValue,
      }).catch(() => {});
    };

    const ensurePlayer = async () => {
      if (!localPlayer) {
        localPlayer = new Player(iframeRef.current);
        vimeoPlayerRef.current = localPlayer;
      }

      await localPlayer.ready();

      const applyStartTime = async () => {
        try {
          const duration = (await localPlayer.getDuration()) || 0;
          const safeTime =
            Number.isFinite(startTime) && duration > 0 && startTime >= duration - 5
              ? 0
              : (Number(startTime) || 0);
          await localPlayer.setCurrentTime(safeTime).catch(() => {});
          lastTimeValueRef.current = safeTime;
          lastDurationRef.current = duration;
          lastSavedRef.current = 0;
          return duration;
        } catch {
          return 0;
        }
      };

      const duration = await applyStartTime();

      detachHandlers();

      const handleTimeUpdate = ({ seconds, duration: d }) => {
        console.log("Vimeo timeupdate:", { seconds, d });
        const currentTime = Math.floor(seconds || 0);
        const effectiveDuration = d || lastDurationRef.current || duration;
        lastTimeValueRef.current = currentTime;
        lastDurationRef.current = effectiveDuration;
        triggerProgressSave(currentTime, effectiveDuration);
      };

      const handlePause = () => {
        const currentTime = lastTimeValueRef.current;
        const effectiveDuration = lastDurationRef.current || duration;
        if (!Number.isFinite(currentTime) || currentTime < 0) return;
        triggerProgressSave(currentTime, effectiveDuration, true);
      };

      const handleEnded = () => {
        if (!videoId) return;
        lastTimeValueRef.current = 0;
        lastDurationRef.current = duration;
        updateView({ videoId, completed: true, lastTime: 0, duration }).catch(() => {});
      };

      vimeoHandlersRef.current = {
        timeupdate: handleTimeUpdate,
        pause: handlePause,
        ended: handleEnded,
      };

      localPlayer.on("timeupdate", handleTimeUpdate);
      localPlayer.on("pause", handlePause);
      localPlayer.on("ended", handleEnded);

      clearIntervalIfAny();
      intervalRef.current = setInterval(async () => {
        if (cancelled || !videoId) return;
        try {
          const [seconds, durationValue] = await Promise.all([
            localPlayer.getCurrentTime(),
            localPlayer.getDuration(),
          ]);
          const currentTime = Math.floor(seconds || 0);
          const effectiveDuration = durationValue || lastDurationRef.current || duration;
          lastTimeValueRef.current = currentTime;
          lastDurationRef.current = effectiveDuration;
          triggerProgressSave(currentTime, effectiveDuration);
        } catch {
          // ignore letture fallite
        }
      }, updateIntervalMs);
    };

    ensurePlayer().catch((e) => console.error("Errore Vimeo setup:", e));

    return () => {
      cancelled = true;
      clearIntervalIfAny();
      detachHandlers();
      if (videoId && lastTimeValueRef.current > 0) {
        triggerProgressSave(lastTimeValueRef.current, lastDurationRef.current || 0, true);
      }
    };
  }, [isVimeo, startTime, updateView, videoId, updateIntervalMs, embedUrl]);

  // --- 4) YouTube
  useEffect(() => {
    const clearTick = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    if (!isYouTube || !iframeRef.current || startTime === null) {
      clearTick();
      if (ytPlayerRef.current?.destroy) ytPlayerRef.current.destroy();
      ytPlayerRef.current = null;
      return;
    }

    let duration = 0;

    const loadYTPlayer = () => {
      ytPlayerRef.current = new window.YT.Player(iframeRef.current, {
        events: {
          onReady: (event) => {
            setTimeout(() => {
              duration = event.target.getDuration();
              const safeTime =
                Number.isFinite(startTime) && duration > 0 && startTime >= duration - 30
                  ? 0
                  : (Number(startTime) || 0);
              event.target.seekTo(safeTime, true);
              event.target.playVideo?.();
            }, 800);
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.ENDED) {
              updateView({ videoId, completed: true, lastTime: 0, duration }).catch(() => {});
            }
          }
        }
      });

      intervalRef.current = setInterval(() => {
        try {
          if (ytPlayerRef.current?.getPlayerState?.() === window.YT.PlayerState.PLAYING) {
            const currentTime = Math.floor(ytPlayerRef.current.getCurrentTime());
            updateView({ videoId, completed: false, lastTime: currentTime, duration }).catch(() => {});
          }
        } catch {}
      }, updateIntervalMs);
    };

    if (!window.YT || !window.YT.Player) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      window.onYouTubeIframeAPIReady = loadYTPlayer;
      document.body.appendChild(tag);
    } else {
      loadYTPlayer();
    }

    return () => {
      clearTick();
      if (ytPlayerRef.current?.destroy) ytPlayerRef.current.destroy();
      ytPlayerRef.current = null;
    };
  }, [isYouTube, startTime, updateView, videoId, updateIntervalMs]);

  if (!video) {
    return (
      <div className="text-white text-center p-6 text-2xl">
        Nessun trailer disponibile
      </div>
    );
  }

  // funzione semplice (non-hook) per aggiungere querystring
  const withQuery = (url, query) => {
    if (!query) return url;
    return url.includes("?") ? `${url}&${query}` : `${url}?${query}`;
  };

  const ytSrc = isYouTube ? withQuery(embedUrl, "enablejsapi=1") : null;
  const vimeoSrc = isVimeo
    ? withQuery(embedUrl, "autoplay=1&muted=0&playsinline=1&controls=1")
    : null;

  return (
    <div className="w-full h-full flex justify-center items-center">
      {isMp4 ? (
        <video
          ref={videoRef}
          controls
          className="w-full h-full object-contain rounded-md"
          onEnded={() => updateView({ videoId, completed: true, lastTime: 0 }).catch(() => {})}
        >
          <source src={embedUrl} type="video/mp4" />
        </video>
      ) : (
        <div className="relative w-full pb-[56.25%]">
          <iframe
            ref={iframeRef}
            src={isYouTube ? ytSrc : vimeoSrc || embedUrl}
            className="absolute top-0 left-0 w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title="Trailer"
          />
        </div>
      )}
    </div>
  );
}

export default VideoTrailer;
